import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Mail, Phone, MapPin, Building2, ArrowUpRight, ShieldCheck, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Enterprise Security Stack",
  "Managed Security (MSSP)",
  "VAPT & Audits",
  "Zero Trust / ZTNA",
  "Data Protection (DLP)",
  "Compliance & GRC",
  "Security Awareness",
  "Workforce / Resources",
  "AI Security / Labs",
];

const INDUSTRIES = ["BFSI", "Healthcare", "Retail", "Manufacturing", "Education", "SaaS / Tech", "Government", "Other"];

const REQUEST_TYPES = {
  consultation: {
    eyebrow: "Request Consultation",
    title: "Let's design your security roadmap",
    description: "A 30–45 minute strategic conversation with a Cosmos Defence specialist on your security posture, gaps and next 90 days.",
  },
  assessment: {
    eyebrow: "Get Security Assessment",
    title: "Start with a security assessment",
    description: "We'll review your environment, identify the most material risks and propose a sequenced remediation plan.",
  },
  support: {
    eyebrow: "Cyber Assistance & Support",
    title: "Get assistance or request technical resources",
    description: "Whether you're a victim of fraud, a small team needing help, or an enterprise requesting technical staffing — share the details and we'll respond within one business day.",
  },
  partnership: {
    eyebrow: "Alliance & Partnership",
    title: "Partner with the Cosmos Defence ecosystem",
    description: "For technology vendors, awareness partners, training collaborators and integrators looking to engage with our ecosystem.",
  },
  inquiry: {
    eyebrow: "Contact",
    title: "Let's secure what matters most",
    description: "Talk to a Cosmos Defence specialist about your security, awareness or workforce needs. We respond within one business day.",
  },
};

export default function Contact() {
  const [params] = useSearchParams();
  const initialType = params.get("type") in REQUEST_TYPES ? params.get("type") : "inquiry";
  const initialService = params.get("service");

  const [form, setForm] = useState({
    full_name: "", company_name: "", business_email: "", phone_number: "",
    industry: "", services: [], message: "", request_type: initialType,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialService === "workforce") {
      setForm((f) => ({ ...f, services: Array.from(new Set([...f.services, "Workforce / Resources"])) }));
    }
    if (initialService === "resources") {
      setForm((f) => ({ ...f, services: Array.from(new Set([...f.services, "Workforce / Resources"])) }));
    }
    if (initialService === "training") {
      setForm((f) => ({ ...f, services: Array.from(new Set([...f.services, "Security Awareness"])) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = REQUEST_TYPES[form.request_type] || REQUEST_TYPES.inquiry;

  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — our team will reach out within one business day.");
      setForm({ full_name: "", company_name: "", business_email: "", phone_number: "", industry: "", services: [], message: "", request_type: form.request_type });
    } catch (err) {
      toast.error("Submission failed. Please email secure@defence.cngtpl.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow={meta.eyebrow} title={meta.title} description={meta.description} />

      <section className="py-16 bg-white">
        <div className="container-cd grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-cd">
              <ShieldCheck size={22} className="text-[#22D3EE]" />
              <h3 className="mt-4 font-display text-xl font-bold text-[#0A1B5E]">Speak with our team</h3>
              <p className="mt-2 text-slate-600 text-sm">For consultations, demos, RFPs, and partnerships.</p>
              <ul className="mt-6 space-y-4">
                <li className="flex items-start gap-3" data-testid="contact-info-email">
                  <Mail size={18} className="text-[#3B82F6] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase font-mono-tech tracking-widest text-slate-400">Email</div>
                    <a className="font-display font-semibold text-[#0A1B5E] hover:text-[#3B82F6]" href="mailto:secure@defence.cngtpl.com">
                      secure@defence.cngtpl.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="contact-info-phone">
                  <Phone size={18} className="text-[#3B82F6] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase font-mono-tech tracking-widest text-slate-400">Phone</div>
                    <a className="font-display font-semibold text-[#0A1B5E] hover:text-[#3B82F6]" href="tel:+919315649601">
                      +91 93156 49601
                    </a>
                    <div className="text-slate-500 text-sm mt-1">
                      <span className="font-mono-tech tracking-wider text-[#0A1B5E]">1930</span> · National Cyber Helpline
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="contact-info-address">
                  <MapPin size={18} className="text-[#3B82F6] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase font-mono-tech tracking-widest text-slate-400">Address</div>
                    <div className="font-display font-semibold text-[#0A1B5E] leading-snug">
                      208, H-73, H Block, Sector 63,<br />Noida, Uttar Pradesh — 201309
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="contact-info-domain">
                  <MapPin size={18} className="text-[#3B82F6] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase font-mono-tech tracking-widest text-slate-400">Domain</div>
                    <div className="font-display font-semibold text-[#0A1B5E]">defence.cngtpl.com</div>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="contact-info-company">
                  <Building2 size={18} className="text-[#3B82F6] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase font-mono-tech tracking-widest text-slate-400">Operated by</div>
                    <div className="font-display font-semibold text-[#0A1B5E]">Cosmos Next Gen Tech Pvt Ltd (CNGTPL)</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="card-cd ring-glow-cyan border-cyan-200 dark-strip-bg text-white">
              <Clock size={22} className="text-cyan-300" />
              <h3 className="mt-4 font-display text-xl font-bold">SLA-backed response</h3>
              <p className="mt-2 text-slate-300 text-sm">Critical inquiries are routed to our SOC team for &lt; 1 hour acknowledgement.</p>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 card-cd" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Request Type</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="contact-request-type-chips">
                  {Object.keys(REQUEST_TYPES).map((k) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`contact-request-type-${k}`}
                      onClick={() => setForm({ ...form, request_type: k })}
                      className={`px-3 py-1.5 rounded-full text-xs font-display font-semibold border transition-all ${
                        form.request_type === k
                          ? "bg-[#0A1B5E] text-white border-[#0A1B5E]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#3B82F6] hover:text-[#3B82F6]"
                      }`}
                    >
                      {REQUEST_TYPES[k].eyebrow}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Full Name *</label>
                <input required data-testid="contact-input-fullname" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Company</label>
                <input data-testid="contact-input-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Business Email *</label>
                <input type="email" required data-testid="contact-input-email" value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Phone</label>
                <input data-testid="contact-input-phone" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Industry</label>
                <select data-testid="contact-select-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none bg-white">
                  <option value="">Select an industry</option>
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Services / Areas of Interest</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="contact-services-chips">
                  {SERVICES.map((s) => {
                    const active = form.services.includes(s);
                    return (
                      <button type="button" key={s}
                        data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => toggleService(s)}
                        className={`px-3 py-1.5 rounded-full text-sm font-display font-medium border transition-all ${
                          active ? "bg-[#0A1B5E] text-white border-[#0A1B5E]"
                            : "bg-white text-slate-600 border-slate-200 hover:border-[#3B82F6] hover:text-[#3B82F6]"
                        }`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Message *</label>
                <textarea required rows={5} data-testid="contact-input-message" value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Share your goals, environment, timelines and anything urgent..."
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none resize-none" />
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-slate-500 max-w-md">
                By submitting, you agree to be contacted by Cosmos Defence. We will not share your data with third parties.
              </p>
              <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="btn-accent">
                {submitting ? "Sending..." : "Send Message"} <ArrowUpRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
